/**
 * Cadastros — espécies, produtos, parceiros e frota.
 *
 * Sem esta tela a serraria não consegue registrar o próprio catálogo: tudo
 * nascia por API ou pelo semeador. Um ERP em que o cliente não cadastra os
 * próprios produtos não é usável, por mais correto que seja o resto.
 */
import { useCallback, useEffect, useState } from "react";
import { REGRAS_CUBAGEM, ehDocumentoValido, ehNcmValido, formatarDocumento, UFS } from "@madeira/dominio";
import { api, podeTudoQue, type Papel, type Parceiro, type Produto } from "../api.ts";
import { Campo, Selecao, Tabela } from "../componentes/Campos.tsx";

type Aba = "especies" | "produtos" | "parceiros" | "frota";

interface Especie {
  id: string; nomeComum: string; nomeCientifico: string | null;
  nativa: boolean; densidadeKgM3: string | null;
}
interface Veiculo {
  id: string; placa: string; descricao: string | null;
  capacidadeM3: string | null; ativo: boolean;
}
interface Motorista { id: string; nome: string; cpf: string; ativo: boolean }

export function Cadastros({
  aviso, aoMudarCadastro, papel,
}: {
  aviso: (t: string) => void;
  aoMudarCadastro: () => void;
  papel: Papel;
}) {
  const [aba, setAba] = useState<Aba>("produtos");

  /**
   * O pátio chega até aqui para CONSULTAR — qual o código do produto, qual a
   * regra de cubagem do fornecedor que acabou de encostar no portão. Gravar é
   * do escritório, porque produto carrega NCM e parceiro carrega regra de
   * cubagem: decisão fiscal e contratual, não de pátio.
   */
  const podeEditar = podeTudoQue(papel, "ESCRITORIO");

  return (
    <>
      <div className="cab-doc">
        <h1>Cadastros</h1>
        <div style={{ display: "flex", gap: 6 }}>
          {([
            ["especies", "Espécies"], ["produtos", "Produtos"],
            ["parceiros", "Parceiros"], ["frota", "Frota"],
          ] as [Aba, string][]).map(([id, nome]) => (
            <button
              key={id}
              className={`bt ${aba === id ? "" : "bt-neutro"}`}
              style={{ padding: "4px 12px", fontSize: 13 }}
              onClick={() => setAba(id)}
            >
              {nome}
            </button>
          ))}
        </div>
      </div>

      <div className="rolagem">
        {!podeEditar && (
          <div className="painel" style={{
            borderColor: "var(--info-borda)", background: "var(--info-fundo)",
            padding: 12, color: "var(--info-texto)", fontSize: 13.5,
          }}>
            Você está vendo o catálogo em <b>modo consulta</b>. Cadastrar e
            alterar é do escritório.
          </div>
        )}
        {aba === "especies" && <Especies aviso={aviso} podeEditar={podeEditar} />}
        {aba === "produtos" && (
          <Produtos aviso={aviso} aoMudar={aoMudarCadastro} podeEditar={podeEditar} />
        )}
        {aba === "parceiros" && (
          <Parceiros aviso={aviso} aoMudar={aoMudarCadastro} podeEditar={podeEditar} />
        )}
        {aba === "frota" && <Frota aviso={aviso} podeEditar={podeEditar} />}
      </div>
    </>
  );
}

/* ------------------------------------------------------------- espécies */

function Especies({ aviso, podeEditar }: { aviso: (t: string) => void; podeEditar: boolean }) {
  const [lista, setLista] = useState<Especie[] | null>(null);
  const [nome, setNome] = useState("");
  const [cientifico, setCientifico] = useState("");
  const [nativa, setNativa] = useState(false);
  const [densidade, setDensidade] = useState("");
  const [ocupado, setOcupado] = useState(false);

  const recarregar = useCallback(() => {
    void api.get<Especie[]>("/especies").then(setLista).catch(() => setLista([]));
  }, []);
  useEffect(recarregar, [recarregar]);

  async function salvar() {
    setOcupado(true);
    try {
      await api.post("/especies", {
        nomeComum: nome, nomeCientifico: cientifico || undefined,
        nativa, densidadeKgM3: densidade || undefined,
      });
      aviso(`Espécie ${nome} cadastrada`);
      setNome(""); setCientifico(""); setDensidade(""); setNativa(false);
      recarregar();
    } catch (e) {
      aviso(e instanceof Error ? e.message : "falha ao cadastrar");
    } finally { setOcupado(false); }
  }

  return (
    <>
      <div className="painel">
        <div className="tt"><span className="rotulo">Nova espécie</span></div>
        <div style={{ padding: 14, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Campo rotulo="Nome comum" largura={200} valor={nome} aoMudar={setNome} />
          <Campo rotulo="Nome científico" largura={220} valor={cientifico} aoMudar={setCientifico} />
          <Campo rotulo="Densidade kg/m³" largura={130} valor={densidade} aoMudar={setDensidade} numero />
          <label style={{ display: "flex", alignItems: "center", gap: 6, height: 34 }}>
            <input type="checkbox" checked={nativa} onChange={(e) => setNativa(e.target.checked)} />
            <span style={{ fontSize: 13 }}>nativa</span>
          </label>
          <button className="bt" onClick={() => void salvar()} disabled={!podeEditar || ocupado || !nome.trim()}>
            Cadastrar
          </button>
        </div>
        {/* Nativa muda o roadmap do cliente: floresta plantada dispensa DOF. */}
        <div style={{ padding: "0 14px 12px", fontSize: 12, color: "var(--texto-terciario)" }}>
          Marcar como nativa importa: madeira nativa exige DOF no transporte, plantada não.
        </div>
      </div>

      <Tabela
        vazio="Nenhuma espécie cadastrada."
        dados={lista}
        colunas={["Nome comum", "Científico", "Densidade", "Origem"]}
        linha={(e: Especie) => [
          <b key="n">{e.nomeComum}</b>,
          <span key="c" style={{ fontStyle: "italic" }}>{e.nomeCientifico ?? "—"}</span>,
          <span key="d" className="mono">{e.densidadeKgM3 ?? "—"}</span>,
          e.nativa
            ? <span key="o" className="pil p-aten"><i />nativa</span>
            : <span key="o" className="pil p-ok"><i />plantada</span>,
        ]}
      />
    </>
  );
}

/* ------------------------------------------------------------- produtos */

function Produtos({ aviso, aoMudar, podeEditar }: { aviso: (t: string) => void; aoMudar: () => void; podeEditar: boolean }) {
  const [lista, setLista] = useState<Produto[] | null>(null);
  const [especies, setEspecies] = useState<Especie[]>([]);
  const [codigo, setCodigo] = useState("");
  const [especieId, setEspecieId] = useState("");
  const [forma, setForma] = useState("SERRADA");
  const [classe, setClasse] = useState("");
  const [ncm, setNcm] = useState("");
  const [ocupado, setOcupado] = useState(false);

  const recarregar = useCallback(() => {
    void api.get<Produto[]>("/produtos").then(setLista).catch(() => setLista([]));
    void api.get<Especie[]>("/especies").then((e) => {
      setEspecies(e);
      setEspecieId((atual) => atual || e[0]?.id || "");
    }).catch(() => {});
  }, []);
  useEffect(recarregar, [recarregar]);

  const ncmValido = ncm === "" || ehNcmValido(ncm);

  async function salvar() {
    setOcupado(true);
    try {
      await api.post("/produtos", {
        codigo, especieId, forma, classe: classe || undefined, ncm,
      });
      aviso(`Produto ${codigo.toUpperCase()} cadastrado`);
      setCodigo(""); setClasse(""); setNcm("");
      recarregar(); aoMudar();
    } catch (e) {
      aviso(e instanceof Error ? e.message : "falha ao cadastrar");
    } finally { setOcupado(false); }
  }

  async function desativar(p: Produto) {
    try {
      await api.del(`/produtos/${p.id}`);
      // Desativa em vez de apagar: há movimento apontando para ele.
      aviso(`${p.codigo} desativado — o histórico continua íntegro`);
      recarregar(); aoMudar();
    } catch (e) {
      aviso(e instanceof Error ? e.message : "falha ao desativar");
    }
  }

  return (
    <>
      <div className="painel">
        <div className="tt"><span className="rotulo">Novo produto</span></div>
        <div style={{ padding: 14, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Campo rotulo="Código" largura={130} valor={codigo} aoMudar={setCodigo} />
          <Selecao rotulo="Espécie" largura={200} valor={especieId} aoMudar={setEspecieId}
            opcoes={especies.map((e) => [e.id, e.nomeComum])} />
          <Selecao rotulo="Forma" largura={140} valor={forma} aoMudar={setForma}
            opcoes={[["TORA", "tora"], ["SERRADA", "serrada"], ["BENEFICIADA", "beneficiada"]]} />
          <Campo rotulo="Classe" largura={130} valor={classe} aoMudar={setClasse} />
          <Campo rotulo="NCM" largura={130} valor={ncm} aoMudar={setNcm} numero
            invalido={!ncmValido} />
          <button className="bt" onClick={() => void salvar()}
            disabled={!podeEditar || ocupado || !codigo.trim() || !especieId || !ehNcmValido(ncm)}>
            Cadastrar
          </button>
        </div>
        {/* A ausência de bitola aqui é a decisão da §2.1 ficando visível. */}
        <div style={{ padding: "0 14px 12px", fontSize: 12, color: "var(--texto-terciario)" }}>
          Não há espessura, largura nem comprimento aqui de propósito: bitola é dado do
          movimento (§2.1). Um SKU por combinação daria milhares de cadastros.
        </div>
      </div>

      <Tabela
        vazio="Nenhum produto cadastrado."
        dados={lista}
        colunas={["Código", "Espécie", "Forma", "Classe", "NCM", "Situação", ""]}
        linha={(p: Produto) => [
          <span key="c" className="mono">{p.codigo}</span>,
          <span key="e">{p.especie}{p.nativa && <span className="pil p-aten" style={{ marginLeft: 6 }}><i />nativa</span>}</span>,
          <span key="f">{p.forma.toLowerCase()}</span>,
          <span key="cl">{p.classe ?? "—"}</span>,
          <span key="n" className="mono">{p.ncm}</span>,
          p.ativo
            ? <span key="s" className="pil p-ok"><i />ativo</span>
            : <span key="s" className="pil p-erro"><i />inativo</span>,
          p.ativo && podeEditar ? (
            <button key="a" className="bt bt-neutro" style={{ padding: "3px 9px", fontSize: 12 }}
              onClick={() => void desativar(p)}>desativar</button>
          ) : <span key="a" />,
        ]}
      />
    </>
  );
}

/* ------------------------------------------------------------ parceiros */

function Parceiros({ aviso, aoMudar, podeEditar }: { aviso: (t: string) => void; aoMudar: () => void; podeEditar: boolean }) {
  const [lista, setLista] = useState<Parceiro[] | null>(null);
  const [papel, setPapel] = useState("CLIENTE");
  const [nome, setNome] = useState("");
  const [documento, setDocumento] = useState("");
  const [uf, setUf] = useState("PR");
  const [cidade, setCidade] = useState("");
  const [regra, setRegra] = useState("SERRADA");
  const [coeficiente, setCoeficiente] = useState("");
  const [ocupado, setOcupado] = useState(false);

  const recarregar = useCallback(() => {
    void api.get<Parceiro[]>("/parceiros").then(setLista).catch(() => setLista([]));
  }, []);
  useEffect(recarregar, [recarregar]);

  const docValido = documento === "" || ehDocumentoValido(documento);
  const precisaCoef = regra === "EMPILHAMENTO";

  async function salvar() {
    setOcupado(true);
    try {
      await api.post("/parceiros", {
        papel, nome, documento, uf, cidade: cidade || undefined,
        regraCubagem: regra,
        ...(precisaCoef ? { coeficienteEmpilhamento: coeficiente } : {}),
      });
      aviso(`${nome} cadastrado · cubagem ${regra}`);
      setNome(""); setDocumento(""); setCidade(""); setCoeficiente("");
      recarregar(); aoMudar();
    } catch (e) {
      aviso(e instanceof Error ? e.message : "falha ao cadastrar");
    } finally { setOcupado(false); }
  }

  async function trocarRegra(p: Parceiro, nova: string) {
    const coef = nova === "EMPILHAMENTO"
      ? window.prompt("Coeficiente de empilhamento (estéreo → m³):") : null;
    if (nova === "EMPILHAMENTO" && !coef) return;
    try {
      const r = await api.put<{ de: string; para: string; documentosComRegraAntiga: number }>(
        `/parceiros/${p.id}/regra-cubagem`,
        { regra: nova, ...(coef ? { coeficiente: coef } : {}) },
      );
      // Snapshot: documentos gravados guardam a regra antiga (§2.4).
      aviso(
        `${p.nome}: ${r.de} → ${r.para}. ` +
        `${r.documentosComRegraAntiga} documento(s) mantêm a regra antiga — snapshot não se reescreve.`,
      );
      recarregar(); aoMudar();
    } catch (e) {
      aviso(e instanceof Error ? e.message : "falha ao trocar a regra");
    }
  }

  return (
    <>
      <div className="painel">
        <div className="tt"><span className="rotulo">Novo parceiro</span></div>
        <div style={{ padding: 14, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Selecao rotulo="Papel" largura={140} valor={papel} aoMudar={setPapel}
            opcoes={[["CLIENTE", "cliente"], ["FORNECEDOR", "fornecedor"], ["AMBOS", "ambos"]]} />
          <Campo rotulo="Nome" largura={220} valor={nome} aoMudar={setNome} />
          <Campo rotulo="CNPJ ou CPF" largura={170} valor={documento} aoMudar={setDocumento}
            invalido={!docValido} />
          <Selecao rotulo="UF" largura={80} valor={uf} aoMudar={setUf}
            opcoes={UFS.map((u) => [u, u])} />
          <Campo rotulo="Cidade" largura={160} valor={cidade} aoMudar={setCidade} />
          <Selecao rotulo="Regra de cubagem" largura={170} valor={regra} aoMudar={setRegra}
            opcoes={REGRAS_CUBAGEM.map((r) => [r, r])} />
          {precisaCoef && (
            <Campo rotulo="Coeficiente" largura={110} valor={coeficiente}
              aoMudar={setCoeficiente} numero />
          )}
          <button className="bt" onClick={() => void salvar()}
            disabled={!podeEditar || ocupado || !nome.trim() || !ehDocumentoValido(documento) || (precisaCoef && !coeficiente)}>
            Cadastrar
          </button>
        </div>
        <div style={{ padding: "0 14px 12px", fontSize: 12, color: "var(--texto-terciario)" }}>
          A regra de cubagem é atributo do <b>contrato</b> (§2.4). Ela vale para documentos
          futuros — os já gravados guardam a regra que tinham.
        </div>
      </div>

      <Tabela
        vazio="Nenhum parceiro cadastrado."
        dados={lista}
        colunas={["Nome", "Papel", "Documento", "Local", "Cubagem"]}
        linha={(p: Parceiro) => [
          <b key="n">{p.nome}</b>,
          <span key="p">{p.papel.toLowerCase()}</span>,
          <span key="d" className="mono">{formatarDocumento(p.documento)}</span>,
          <span key="l">{p.cidade ? `${p.cidade}/${p.uf}` : (p.uf ?? "—")}</span>,
          <select key="r" className="campo-texto foco-claro"
            style={{ width: "auto", padding: "2px 6px", fontSize: 12 }}
            value={p.regraCubagem}
            onChange={(e) => void trocarRegra(p, e.target.value)}>
            {REGRAS_CUBAGEM.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>,
        ]}
      />
    </>
  );
}

/* ---------------------------------------------------------------- frota */

function Frota({ aviso, podeEditar }: { aviso: (t: string) => void; podeEditar: boolean }) {
  const [veiculos, setVeiculos] = useState<Veiculo[] | null>(null);
  const [motoristas, setMotoristas] = useState<Motorista[] | null>(null);
  const [placa, setPlaca] = useState("");
  const [descricao, setDescricao] = useState("");
  const [capacidade, setCapacidade] = useState("");
  const [nomeMot, setNomeMot] = useState("");
  const [cpf, setCpf] = useState("");

  const recarregar = useCallback(() => {
    void api.get<Veiculo[]>("/veiculos").then(setVeiculos).catch(() => setVeiculos([]));
    void api.get<Motorista[]>("/motoristas").then(setMotoristas).catch(() => setMotoristas([]));
  }, []);
  useEffect(recarregar, [recarregar]);

  const cpfValido = cpf === "" || ehDocumentoValido(cpf);

  async function salvarVeiculo() {
    try {
      await api.post("/veiculos", {
        placa, descricao: descricao || undefined, capacidadeM3: capacidade || undefined,
      });
      aviso(`Veículo ${placa.toUpperCase()} cadastrado`);
      setPlaca(""); setDescricao(""); setCapacidade(""); recarregar();
    } catch (e) { aviso(e instanceof Error ? e.message : "falha"); }
  }

  async function salvarMotorista() {
    try {
      await api.post("/motoristas", { nome: nomeMot, cpf });
      aviso(`Motorista ${nomeMot} cadastrado`);
      setNomeMot(""); setCpf(""); recarregar();
    } catch (e) { aviso(e instanceof Error ? e.message : "falha"); }
  }

  return (
    <>
      <div className="painel">
        <div className="tt"><span className="rotulo">Novo veículo</span></div>
        <div style={{ padding: 14, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Campo rotulo="Placa" largura={130} valor={placa} aoMudar={setPlaca} />
          <Campo rotulo="Descrição" largura={200} valor={descricao} aoMudar={setDescricao} />
          <Campo rotulo="Capacidade m³" largura={130} valor={capacidade} aoMudar={setCapacidade} numero />
          <button className="bt" onClick={() => void salvarVeiculo()} disabled={!podeEditar || !placa.trim()}>
            Cadastrar
          </button>
        </div>
        <div style={{ padding: "0 14px 12px", fontSize: 12, color: "var(--texto-terciario)" }}>
          A capacidade é conferida ao fechar o romaneio — melhor recusar antes de carregar.
        </div>
      </div>

      <Tabela vazio="Nenhum veículo." dados={veiculos}
        colunas={["Placa", "Descrição", "Capacidade m³"]}
        linha={(v: Veiculo) => [
          <span key="p" className="mono">{v.placa}</span>,
          <span key="d">{v.descricao ?? "—"}</span>,
          <span key="c" className="mono">{v.capacidadeM3 ?? "—"}</span>,
        ]} />

      <div className="painel">
        <div className="tt"><span className="rotulo">Novo motorista</span></div>
        <div style={{ padding: 14, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Campo rotulo="Nome" largura={220} valor={nomeMot} aoMudar={setNomeMot} />
          <Campo rotulo="CPF" largura={160} valor={cpf} aoMudar={setCpf} invalido={!cpfValido} />
          <button className="bt" onClick={() => void salvarMotorista()}
            disabled={!podeEditar || !nomeMot.trim() || !ehDocumentoValido(cpf)}>
            Cadastrar
          </button>
        </div>
      </div>

      <Tabela vazio="Nenhum motorista." dados={motoristas}
        colunas={["Nome", "CPF"]}
        linha={(m: Motorista) => [
          <b key="n">{m.nome}</b>,
          <span key="c" className="mono">{formatarDocumento(m.cpf)}</span>,
        ]} />
    </>
  );
}
